import React from 'react';
import { Box, Card, Grid, Skeleton } from '@mui/material';

interface LoadingStateProps {
  /** cards: a media grid; list: divided rows; table: a header and rows; detail: a page body. */
  variant?: 'cards' | 'list' | 'table' | 'detail';
  count?: number;
  /** Read by screen readers while content loads. */
  label?: string;
}

/**
 * Skeletons shaped like the content that is coming, shown inside the real
 * page layout (header and all) instead of a bare full-page spinner.
 */
const LoadingState: React.FC<LoadingStateProps> = ({ variant = 'list', count = 4, label = 'Loading' }) => {
  const items = Array.from({ length: count }, (_, i) => i);
  return (
    <Box role="status" aria-live="polite" aria-label={label}>
      {variant === 'cards' && (
        <Grid container spacing={2.5}>
          {items.map((i) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
              <Card>
                <Skeleton variant="rectangular" sx={{ width: '100%', height: 0, pt: '56.25%' }} />
                <Box sx={{ p: 2 }}>
                  <Skeleton width="70%" />
                  <Skeleton width="40%" />
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      {(variant === 'list' || variant === 'table') && (
        <Card>
          {variant === 'table' && (
            <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'grey.100' }}>
              <Skeleton width="35%" />
            </Box>
          )}
          {items.map((i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.75, borderTop: i === 0 && variant === 'list' ? 0 : 1, borderColor: 'divider' }}>
              <Skeleton variant="rounded" width={36} height={36} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width="45%" />
                <Skeleton width="25%" />
              </Box>
              <Skeleton variant="rounded" width={88} height={24} />
            </Box>
          ))}
        </Card>
      )}
      {variant === 'detail' && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rounded" sx={{ width: '100%', height: 0, pt: '56.25%', mb: 2 }} />
            <Skeleton width="60%" />
            <Skeleton width="80%" />
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 2.5 }}>
              {items.map((i) => (
                <Skeleton key={i} width={`${60 + ((i * 13) % 30)}%`} sx={{ mb: 1 }} />
              ))}
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default LoadingState;
